import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';
import { toast } from '../components/ui/Toast';
import type { CreateMedidaInput, UpdateMedidaInput } from '@shared/validation/project-schemas';

// ── DTOs ──

export interface MedidaDTO {
  id: string;
  project_id: string;
  medida_set_id: string | null;
  rubro_tipo: string;
  item_tipo: string;
  recipe_id: string | null;
  recipe_codigo: string | null;
  aplicacion_id: string | null;
  aplicacion_nombre: string | null;
  cantidad: number;
  unidad: string;
  notas: string | null;
  created_by: { id: string; nombre: string };
  created_at: string;
  updated_at: string;
}

// ── List ──

export function useMedidas(
  projectId: string | undefined,
  rubroTipo?: string,
  withoutSet?: boolean,
  setId?: string | null,
) {
  return useQuery({
    queryKey: queryKeys.medidas.list(projectId ?? '', {
      ...(rubroTipo ? { rubro_tipo: rubroTipo } : {}),
      ...(setId ? { set_id: setId } : {}),
      ...(withoutSet ? { without_set: 'true' } : {}),
    }),
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (rubroTipo) params.rubro_tipo = rubroTipo;
      if (setId) params.set_id = setId;
      else if (withoutSet) params.without_set = 'true';
      const response = await api.get<{ data: MedidaDTO[] }>(
        `/projects/${projectId}/medidas`,
        { params },
      );
      return response.data.data;
    },
    enabled: !!projectId,
  });
}

// ── Create ──

export function useCreateMedida() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, data }: { projectId: string; data: Omit<CreateMedidaInput, 'projectId'> }) => {
      const response = await api.post<{ data: MedidaDTO }>(
        `/projects/${projectId}/medidas`,
        data,
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.medidas.byProject(variables.projectId) });
      toast('success', 'Medida registrada');
    },
  });
}

// ── Update ──

export function useUpdateMedida() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: { medidaId: string; projectId: string; data: UpdateMedidaInput }) => {
      const response = await api.put<{ data: MedidaDTO }>(`/medidas/${vars.medidaId}`, vars.data);
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.medidas.byProject(variables.projectId) });
      toast('success', 'Medida actualizada');
    },
  });
}

// ── Delete ──

export function useDeleteMedida() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: { medidaId: string; projectId: string; silent?: boolean }) => {
      await api.delete(`/medidas/${vars.medidaId}`);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.medidas.byProject(variables.projectId) });
      if (!variables.silent) {
        toast('success', 'Medida eliminada');
      }
    },
  });
}

// ── Submit (notify administrativo that medidas are ready) ──

export function useSubmitMedidas() {
  return useMutation({
    mutationFn: async (projectId: string) => {
      await api.post(`/projects/${projectId}/medidas/submit`);
    },
    onSuccess: () => {
      toast('success', 'Medidas enviadas al administrativo');
    },
  });
}

// ── Batch upsert (recipe or manual items) ──

export interface BatchMedidaItem {
  recipeId?: string | null;
  aplicacionId?: string | null;
  codigo?: string;
  rubroTipo: string;
  itemTipo: string;
  cantidad: number;
  unidad: string;
}

export function useBatchUpsertMedidas() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      projectId: string;
      medidaSetId?: string | null;
      items: BatchMedidaItem[];
      notas?: string | null;
    }) => {
      const response = await api.put<{ data: { created: number; updated: number; deleted: number } }>(
        `/projects/${vars.projectId}/medidas/batch`,
        { items: vars.items, notas: vars.notas, medidaSetId: vars.medidaSetId ?? null },
      );
      return response.data.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.medidas.byProject(variables.projectId) });
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['medida-sets', 'list', variables.projectId] });
      const parts = [];
      if (data.created > 0) parts.push(`${data.created} creadas`);
      if (data.updated > 0) parts.push(`${data.updated} actualizadas`);
      if (data.deleted > 0) parts.push(`${data.deleted} eliminadas`);
      toast('success', `Medidas guardadas: ${parts.join(', ') || 'sin cambios'}`);
    },
  });
}
